import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Webhook, AlertCircle, Link2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SamplePayloadViewer from '../../components/integrations/SamplePayloadViewer';
import FieldMappingEditor from '../../components/integrations/FieldMappingEditor';
import { integrationsApi } from '../../services/integrations-api';
import type { SaveWebhookMappingRequest } from '../../services/integrations-api';
import { handleApiError } from '../../services/api';

const WebhookEditConfig: React.FC = () => {
  const navigate = useNavigate();
  const { dataSourceId } = useParams<{ dataSourceId: string }>();
  const queryClient = useQueryClient();

  const { data: configData, isLoading, error } = useQuery({
    queryKey: ['webhookConfig', dataSourceId],
    queryFn: () => integrationsApi.getWebhookMappingConfig(dataSourceId!),
    enabled: !!dataSourceId,
  });

  const saveMappingMutation = useMutation({
    mutationFn: async (mapping: SaveWebhookMappingRequest) => {
      return await integrationsApi.saveWebhookMapping(dataSourceId!, mapping);
    },
    onSuccess: () => {
      toast.success('Webhook configuration updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['dataSourceSchedules'] });
      navigate('/sources');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to save configuration');
    },
  });

  const handleSaveMapping = (mapping: SaveWebhookMappingRequest) => {
    saveMappingMutation.mutate(mapping);
  };

  const handleCopyWebhookUrl = () => {
    if (configData?.webhookUrl) {
      navigator.clipboard.writeText(configData.webhookUrl);
      toast.success('Webhook URL copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !configData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
          <Button variant="ghost" onClick={() => navigate('/sources')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Error Loading Configuration
            </h2>
            <p className="text-muted-foreground">
              {handleApiError(error) || 'Failed to load webhook configuration. Please try again.'}
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (configData.sourceType !== 'webhook') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
          <Button variant="ghost" onClick={() => navigate('/sources')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-warn mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Not a Webhook Data Source
            </h2>
            <p className="text-muted-foreground">
              This data source is of type &quot;{configData.sourceType}&quot; and cannot be configured as a webhook.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (!configData.samplePayload) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
          <Button variant="ghost" onClick={() => navigate('/sources')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-warn mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              No Sample Payload
            </h2>
            <p className="text-muted-foreground mb-4">
              This webhook hasn&apos;t received a sample payload yet. Send a test webhook first.
            </p>
            {configData.webhookUrl && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Webhook URL:</p>
                <code className="text-sm text-primary break-all">
                  {configData.webhookUrl}
                </code>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // Build initial mapping in the new SaveWebhookMappingRequest shape
  const initialMapping: SaveWebhookMappingRequest | null = configData.mapping
    ? {
        transactionMappings: configData.mapping.transactionMappings,
        detailMappings: configData.mapping.detailMappings,
        detailsArrayPath: configData.mapping.detailsArrayPath,
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/sources')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </Button>
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-primary/15 rounded-lg">
              <Webhook className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">
              Edit Webhook Configuration
            </h1>
          </div>
          <p className="text-muted-foreground">
            Modify the field mappings for <strong>{configData.name}</strong>
          </p>
        </div>

        {/* Webhook URL */}
        {configData.webhookUrl && (
          <Card className="p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Link2 className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Webhook URL</p>
                  <code className="text-sm text-primary break-all">
                    {configData.webhookUrl}
                  </code>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleCopyWebhookUrl}>
                Copy
              </Button>
            </div>
          </Card>
        )}

        {/* Sample Payload */}
        <Card className="p-6 mb-6">
          <SamplePayloadViewer payload={configData.samplePayload} />
          {configData.sampleCapturedAt && (
            <p className="text-sm text-muted-foreground mt-2">
              Sample captured: {new Date(configData.sampleCapturedAt).toLocaleString()}
            </p>
          )}
        </Card>

        {/* Field Mapping Editor */}
        <Card className="p-6">
          <FieldMappingEditor
            sample={configData.samplePayload}
            dataSourceId={dataSourceId!}
            onSave={handleSaveMapping}
            isLoading={saveMappingMutation.isPending}
            initialMapping={initialMapping}
            isEditMode={true}
          />
        </Card>
      </div>
    </div>
  );
};

export default WebhookEditConfig;
